/**
 * API helper for router configuration backups.
 * Base: API_BASE + '/auth/backups'
 */
import { apiFetch, readErrorMessage } from 'api/client';

const BASE = () => '/auth/backups';

const routerPath = (routerId) => `${BASE()}/routers/${encodeURIComponent(routerId)}`;

const query = (params) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
    });
    const s = qs.toString();
    return s ? `?${s}` : '';
};

const getJSON = async (path, fallback) => {
    const res = await apiFetch(path);
    if (!res.ok) throw new Error(await readErrorMessage(res, fallback));
    return res.json();
};

export const fetchRouters = (organizationId) =>
    getJSON(`${BASE()}/routers${query({ organization_id: organizationId })}`, 'Failed to load routers');

export const fetchGenerations = (routerId) =>
    getJSON(`${routerPath(routerId)}/generations`, 'Failed to load repositories');

export const fetchCommits = (routerId, { generation, skip = 0, limit = 50 } = {}) =>
    getJSON(`${routerPath(routerId)}/commits${query({ generation, skip, limit })}`, 'Failed to load history');

export const fetchTree = (routerId, sha, generation) =>
    getJSON(`${routerPath(routerId)}/commits/${encodeURIComponent(sha)}/tree${query({ generation })}`, 'Failed to load files');

export const fetchFile = (routerId, sha, path, generation) =>
    getJSON(`${routerPath(routerId)}/commits/${encodeURIComponent(sha)}/file${query({ path, generation })}`, 'Failed to load file');

export const fetchDiff = (routerId, { to, from, path, generation }) =>
    getJSON(`${routerPath(routerId)}/diff${query({ to, from, path, generation })}`, 'Failed to load changes');

export const fetchAccessLog = (routerId) =>
    getJSON(`${routerPath(routerId)}/access-log`, 'Failed to load access log');

export const startGeneration = async (routerId) => {
    const res = await apiFetch(`${routerPath(routerId)}/generations`, { method: 'POST' });
    if (!res.ok) throw new Error(await readErrorMessage(res, 'Failed to start a new repository'));
    return res.json();
};

/** Downloads one backup as the tar.gz `netc import` restores from. */
export const downloadArchive = async (routerId, sha, generation) => {
    const res = await apiFetch(`${routerPath(routerId)}/commits/${encodeURIComponent(sha)}/archive${query({ generation })}`);
    if (!res.ok) throw new Error(await readErrorMessage(res, 'Failed to download backup'));
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${routerId}-${sha.slice(0, 12)}.tar.gz`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
